import { QUICK_SLOT_ANCHORS } from "./constants";
import { normalizeTime } from "./slot-gating";
import type { TimeSlot } from "./types";

export type QuickSplit<T> = {
	/** Compact row shown inline. At most `QUICK_SLOT_ANCHORS.length` entries. */
	quick: T[];
	/** Everything else, chronological — lives behind the "more times" popover. */
	rest: T[];
};

/** "13:30" -> 810. Returns null for anything unparseable. */
function toMinutes(time: string): number | null {
	const [h, m] = normalizeTime(time).split(":");
	const hours = Number(h);
	const minutes = Number(m);

	if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
		return null;
	}

	return hours * 60 + minutes;
}

/**
 * Picks the time closest to `target` among `pool`, skipping anything already in
 * `taken`. Ties go to the earlier time, since `pool` is chronological and the
 * comparison is strict.
 */
function nearestTime(
	pool: string[],
	target: number,
	taken: Set<string>,
): string | null {
	let best: string | null = null;
	let bestDistance = Number.POSITIVE_INFINITY;

	for (const time of pool) {
		if (taken.has(time)) {
			continue;
		}

		const minutes = toMinutes(time);

		if (minutes === null) {
			continue;
		}

		const distance = Math.abs(minutes - target);

		if (distance < bestDistance) {
			best = time;
			bestDistance = distance;
		}
	}

	return best;
}

/**
 * Splits a list of "HH:mm" times into a compact quick-pick row plus the rest.
 *
 * Both schedule modes render far more times than a card or a form column can
 * hold — the calendar mode stacks 20+ server slots per therapist, the legacy
 * "cari terapis" mode carries the full 48-entry half-hour list. Both surface
 * one time per anchor hour and hide the remainder behind a popover, so the two
 * modes read the same and neither can grow unbounded.
 *
 * Anchors are matched by nearest available time, not exact equality: a therapist
 * free 09:15–12:00 still gets a spread across the row rather than falling back
 * to the first N times, which would bunch every chip in the morning.
 *
 * `selected` is always pinned into `quick` — it displaces the nearest anchor
 * pick — so a time chosen from the popover stays visible in the row. A
 * `selected` that is not in `times` is ignored.
 */
export function splitQuickTimes(
	times: string[],
	selected?: string | null,
): QuickSplit<string> {
	const sorted = [...times].sort((a, b) => a.localeCompare(b));

	if (sorted.length <= QUICK_SLOT_ANCHORS.length) {
		return { quick: sorted, rest: [] };
	}

	const taken = new Set<string>();
	const picked: string[] = [];

	for (const anchor of QUICK_SLOT_ANCHORS) {
		const target = toMinutes(anchor);

		if (target === null) {
			continue;
		}

		const time = nearestTime(sorted, target, taken);

		if (time) {
			taken.add(time);
			picked.push(time);
		}
	}

	if (selected && sorted.includes(selected) && !taken.has(selected)) {
		const target = toMinutes(selected);
		// Drop the pick nearest the selection so the row keeps its length and the
		// displaced anchor is the one the selection best stands in for.
		const displaced =
			target === null ? picked.at(-1) : nearestTime(picked, target, new Set());

		if (displaced) {
			taken.delete(displaced);
			picked.splice(picked.indexOf(displaced), 1);
		}

		taken.add(selected);
		picked.push(selected);
	}

	return {
		quick: picked.sort((a, b) => a.localeCompare(b)),
		rest: sorted.filter((time) => !taken.has(time)),
	};
}

/**
 * `splitQuickTimes` over a therapist's slot objects, keyed by `start_time`.
 * Used by the calendar mode's therapist cards.
 */
export function splitQuickSlots(
	slots: TimeSlot[],
	selectedStartTime?: string | null,
): QuickSplit<TimeSlot> {
	const byStartTime = new Map(slots.map((slot) => [slot.start_time, slot]));
	const { quick, rest } = splitQuickTimes(
		[...byStartTime.keys()],
		selectedStartTime,
	);
	const toSlots = (times: string[]) =>
		times
			.map((time) => byStartTime.get(time))
			.filter((slot): slot is TimeSlot => !!slot);

	return { quick: toSlots(quick), rest: toSlots(rest) };
}
